import { createElement } from 'react';

import {
  Block,
  BlockNoteEditor,
  BlockNoteEditorOptions,
  BlockNoteSchema,
  BlockSchema,
  InlineContent,
  InlineContentFromConfig,
  InlineContentSchema,
  StyledText,
  StyleSchema,
  defaultInlineContentSpecs,
  isStyledTextInlineContent,
  markdownToHTML,
} from '@blocknote/core';
import {
  DefaultReactSuggestionItem,
  blockTypeSelectItems,
  getDefaultReactSlashMenuItems,
} from '@blocknote/react';
import { t } from 'i18next';
import { LocateFixedIcon } from 'lucide-react';
import { toast } from 'sonner';

import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import {
  ContextVariable,
  ContextVariableTokenHighlight,
  formatContextVariableDisplay,
} from '@/hooks/ContextVariable';
import type { ContextVariableOption } from '@/shared/types/autocomplete';
import type { Dialog } from '@/shared/types/dialog';
import type { Option } from '@/shared/types/rules';

import { LocalToolBadge } from './LocalToolInlineContent';
import { LOCAL_TOOL_DEFINITIONS } from './localToolTypes';

export const schema = BlockNoteSchema.create({
  inlineContentSpecs: {
    ...defaultInlineContentSpecs,
    mention: ContextVariable,
    contextToken: ContextVariableTokenHighlight,
    localTool: LocalToolBadge,
  },
});

export type BlockNoteEditorInstance = BlockNoteEditor<
  typeof schema.blockSchema,
  typeof schema.inlineContentSchema,
  typeof schema.styleSchema
>;

type EditorBlockSchema = typeof schema.blockSchema;
type EditorInlineContentSchema = typeof schema.inlineContentSchema;
type EditorStyleSchema = typeof schema.styleSchema;

type SchemaInlineContent = InlineContent<EditorInlineContentSchema, EditorStyleSchema>;
type EditorBlock = Block<EditorBlockSchema, EditorInlineContentSchema, EditorStyleSchema>;
type EditorTextNode = StyledText<EditorStyleSchema>;
type EditorMentionNode = InlineContentFromConfig<
  (typeof ContextVariable)['config'],
  EditorStyleSchema
>;
type EditorContextTokenNode = InlineContentFromConfig<
  (typeof ContextVariableTokenHighlight)['config'],
  EditorStyleSchema
>;
type EditorLocalToolNode = InlineContentFromConfig<
  (typeof LocalToolBadge)['config'],
  EditorStyleSchema
>;
type EditorInlineContent =
  SchemaInlineContent | EditorMentionNode | EditorContextTokenNode | EditorLocalToolNode;
type EditorInlineBlock = Extract<EditorBlock, { content: EditorInlineContent[] }>;

export type ContextVariableLookupValue = { value: string; label: string };

export type ContextVariableLookup = Map<string, ContextVariableLookupValue>;

/**
 * Safely normalize contextual token patterns into raw identifiers.
 * @param token Raw token string pulled from editor content.
 * @returns Lowercased token without braces, prefixes, or surrounding whitespace.
 */
export const sanitizeContextToken = (token: string | undefined): string =>
  token?.replace(/[{}]/g, '').replace(/^[$]+/, '').trim() ?? '';

/**
 * Build a case-insensitive lookup for resolving context variable mentions.
 * @param options Variable options with optional label/value fields.
 * @returns Map keyed by sanitized labels pointing to descriptor metadata.
 */
export const buildContextVariableLookup = (options: Option[]): ContextVariableLookup => {
  const lookup: ContextVariableLookup = new Map();

  options.forEach((option) => {
    const sanitizedValue = sanitizeContextToken(option.value);
    const sanitizedLabel = sanitizeContextToken(option.label);
    const resolvedLabel = option.label?.trim() || (sanitizedValue ? `{{${sanitizedValue}}}` : '');
    const descriptor: ContextVariableLookupValue = {
      value: option.value || '',
      label: resolvedLabel,
    };

    if (sanitizedValue) {
      lookup.set(sanitizedValue.toLowerCase(), descriptor);
    }

    if (sanitizedLabel && sanitizedLabel.toLowerCase() !== sanitizedValue.toLowerCase()) {
      lookup.set(sanitizedLabel.toLowerCase(), descriptor);
    }
  });

  return lookup;
};

/**
 * Convert context variables into slash menu suggestions.
 * @param editor Active BlockNote editor instance.
 * @param variables Available context variable options.
 * @returns Suggestion entries that insert mention nodes on selection.
 */
export const getContextVariableMenuItems = (
  editor: BlockNoteEditorInstance,
  variables: ContextVariableOption[]
): DefaultReactSuggestionItem[] => {
  return variables.map((variable) => {
    const sanitizedValue = sanitizeContextToken(variable.value);
    const sanitizedLabel = sanitizeContextToken(variable.label);
    const fallbackToken = sanitizedLabel || sanitizedValue;
    const menuLabel = variable.label.trim()
      ? variable.label
      : fallbackToken
        ? `{{${fallbackToken}}}`
        : '{{}}';
    const aliases = [menuLabel, variable.value, sanitizedValue, sanitizedLabel].filter(
      (alias): alias is string => Boolean(alias)
    );

    return {
      title: menuLabel,
      group: 'Context variables',
      aliases,
      onItemClick: () => {
        editor.insertInlineContent([
          { type: 'mention', props: { contextVariable: variable.value, label: menuLabel } },
          ' ',
        ]);
      },
    } satisfies DefaultReactSuggestionItem;
  });
};

/**
 * Build suggestion menu items for local tools triggered by the `@` character.
 * For tools without arguments, clicking inserts a badge directly.
 * For trigger_workflow, clicking invokes the provided callback to open a workflow picker.
 */
export const getLocalToolMenuItems = (
  editor: BlockNoteEditorInstance,
  enabledTools: string[],
  callbacks: { onEmptyStateClick: () => void; onBeforeWorkflowOpen?: () => void }
): DefaultReactSuggestionItem[] => {
  const definitions = LOCAL_TOOL_DEFINITIONS.filter((def) => enabledTools.includes(def.tool));

  if (definitions.length === 0) {
    return [
      {
        title: `${t('local_tools.no_tools_available')} ${t('local_tools.no_tools_link')}`,
        group: t('local_tools.title'),
        onItemClick: callbacks.onEmptyStateClick,
      },
    ];
  }

  return definitions.map((def) => ({
    title: t(def.labelKey),
    icon: createElement(McpIcon, { size: 16, color: 'var(--muted-foreground)' }),
    group: t('local_tools.title'),
    aliases: [def.tool, t(def.labelKey)],
    onItemClick: () => {
      if (def.requiresArgument) {
        callbacks.onBeforeWorkflowOpen?.();
        editor.openSuggestionMenu('^', { deleteTriggerCharacter: false });

        return;
      }

      editor.insertInlineContent([
        {
          type: 'localTool' as const,
          props: { toolName: def.tool, argument: '', argumentLabel: '' },
        },
        ' ',
      ]);
    },
  }));
};

/**
 * Build suggestion menu items for workflow selection triggered by the `^` character.
 * Each dialog becomes a selectable item that inserts a trigger_workflow badge.
 */
export const getWorkflowMenuItems = (
  editor: BlockNoteEditorInstance,
  dialogs: Dialog[]
): DefaultReactSuggestionItem[] =>
  dialogs
    .filter((d) => d.nodes?.[0]?.node_id)
    .map((dialog) => ({
      title: dialog.name,
      icon: createElement(LocateFixedIcon, { size: 16, color: 'var(--muted-foreground)' }),
      group: t('local_tools.workflow_picker_title'),
      aliases: [dialog.name],
      onItemClick: () => {
        editor.insertInlineContent([
          {
            type: 'localTool' as const,
            props: {
              toolName: 'trigger_workflow',
              argument: dialog.nodes[0].node_id,
              argumentLabel: dialog.name,
            },
          },
          ' ',
        ]);
      },
    }));

/**
 * Create a text node with preserved styling when content exists.
 * @param text Text segment extracted from an inline node.
 * @param styles Style payload to retain on the new node.
 * @returns Styled text node or null if empty.
 */
const createTextNode = (text: string, styles: EditorTextNode['styles']): EditorTextNode | null => {
  if (!text) {
    return null;
  }

  return { type: 'text', text, styles };
};

/**
 * Generate a mention node from a lookup descriptor.
 * @param param0 Descriptor holding raw value and resolved label.
 * @returns BlockNote mention node referencing the context variable.
 */
const createMentionNode = ({ value, label }: ContextVariableLookupValue): EditorMentionNode => ({
  type: 'mention',
  props: { contextVariable: value, label: label || sanitizeContextToken(value) },
  content: undefined,
});

/**
 * Wrap token text in an atomic highlight inline content node to visually flag unresolved variables.
 * @param tokenText Raw token including surrounding braces.
 * @returns Atomic highlight inline content node — props carry the token data.
 */
const createContextTokenNode = (tokenText: string): EditorContextTokenNode => ({
  type: 'contextToken',
  props: { token: sanitizeContextToken(tokenText), rawToken: tokenText },
  content: undefined,
});

/** Set of valid tool names for local tool normalization. */
const VALID_LOCAL_TOOLS = new Set(LOCAL_TOOL_DEFINITIONS.map((def) => def.tool as string));

/** Regex matching `@tool_name(argument)` or `@tool_name` patterns in text. */
const LOCAL_TOOL_NAMES = LOCAL_TOOL_DEFINITIONS.map((d) => d.tool as string).join('|');
const LOCAL_TOOL_REGEX = new RegExp(
  `@(${LOCAL_TOOL_NAMES})(?:\\(((?:[^()]*|\\([^()]*\\))*)\\))?`,
  'g'
);

/**
 * Create a localTool inline node from matched text.
 */
const createLocalToolNode = (
  toolName: string,
  argument: string,
  argumentLabel: string
): EditorLocalToolNode => ({
  type: 'localTool',
  props: { toolName, argument, argumentLabel },
  content: undefined,
});

/**
 * Replace `@tool_name(arg)` patterns in a text node with localTool inline nodes.
 * @param textNode Styled text node to inspect.
 * @returns Generated segments and change flag.
 */
const convertLocalToolTextNode = (
  textNode: EditorTextNode
): { segments: EditorInlineContent[]; changed: boolean } => {
  const segments: EditorInlineContent[] = [];
  let lastIndex = 0;
  let changed = false;

  LOCAL_TOOL_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = LOCAL_TOOL_REGEX.exec(textNode.text)) !== null) {
    const toolName = match[1];
    const rawArgument = match[2] || '';
    // Extract node_id from `node_id="..."` format (trigger_workflow), falling back to raw for legacy
    const argument =
      toolName === 'trigger_workflow'
        ? (rawArgument.match(/^node_id="(.+)"$/)?.[1] ?? rawArgument)
        : rawArgument;

    if (!VALID_LOCAL_TOOLS.has(toolName)) {
      continue;
    }

    const leadingText = textNode.text.slice(lastIndex, match.index);
    const leadingNode = createTextNode(leadingText, textNode.styles);
    if (leadingNode) {
      segments.push(leadingNode);
    }

    segments.push(createLocalToolNode(toolName, argument, argument));
    changed = true;
    lastIndex = LOCAL_TOOL_REGEX.lastIndex;
  }

  if (!changed) {
    return { segments: [], changed: false };
  }

  const trailingText = textNode.text.slice(lastIndex);
  const trailingNode = createTextNode(trailingText, textNode.styles);
  if (trailingNode) {
    segments.push(trailingNode);
  }

  const lastSegment = segments[segments.length - 1];
  if (lastSegment && lastSegment.type === 'localTool') {
    segments.push({ type: 'text', text: ' ', styles: textNode.styles });
  }

  return { segments, changed: true };
};

/**
 * Narrow a block to inline content blocks.
 * @param block BlockNote block candidate.
 * @returns Whether the block content is an inline array.
 */
const isInlineBlock = (block: EditorBlock): block is EditorInlineBlock =>
  Array.isArray(block.content);

/**
 * Determine whether an inline segment represents a highlighted context token.
 * @param segment Inline content candidate.
 * @returns True when the segment is the custom highlight inline content type.
 */
const isContextTokenInlineContent = (
  segment: EditorInlineContent
): segment is EditorContextTokenNode =>
  typeof segment === 'object' && segment !== null && segment['type'] === 'contextToken';

/**
 * Replace `{{token}}` sequences with structured mention nodes or highlight wrappers.
 * @param textNode Styled text node to inspect.
 * @param lookup Case-insensitive context variable lookup.
 * @returns Generated segments and change flag describing replacements.
 */
const convertTextNode = (
  textNode: EditorTextNode,
  lookup: ContextVariableLookup
): { segments: EditorInlineContent[]; changed: boolean } => {
  const segments: EditorInlineContent[] = [];
  let lastIndex = 0;
  let changed = false;

  const regex = /\{\{([^}]+)\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(textNode.text)) !== null) {
    const rawLabel = match[1];
    const normalizedLabel = sanitizeContextToken(rawLabel).toLowerCase();
    const contextVariable = normalizedLabel ? lookup.get(normalizedLabel) : undefined;
    const tokenText = match[0];
    const isManualPlaceholder = /\{\{\s*\$/.test(tokenText);

    const leadingText = textNode.text.slice(lastIndex, match.index);
    const leadingNode = createTextNode(leadingText, textNode.styles);
    if (leadingNode) {
      segments.push(leadingNode);
    }

    if (contextVariable) {
      segments.push(createMentionNode(contextVariable));
      changed = true;
    } else if (isManualPlaceholder) {
      segments.push(createContextTokenNode(tokenText));
      changed = true;
    } else {
      const tokenNode = createTextNode(tokenText, textNode.styles);
      if (tokenNode) {
        segments.push(tokenNode);
      }
    }
    lastIndex = regex.lastIndex;
  }

  if (!changed) {
    return { segments: [], changed: false };
  }

  const trailingText = textNode.text.slice(lastIndex);
  const trailingNode = createTextNode(trailingText, textNode.styles);
  if (trailingNode) {
    segments.push(trailingNode);
  }

  // Add space after atomic inline content (mention/contextToken) for cursor positioning
  // Without this, cursor jumps before the badge instead of after
  const lastSegment = segments[segments.length - 1];
  if (lastSegment && (lastSegment.type === 'mention' || lastSegment.type === 'contextToken')) {
    segments.push({ type: 'text', text: ' ', styles: textNode.styles });
  }

  return { segments, changed: true };
};

/**
 * Normalize inline segments by converting recognized tokens into mentions or highlight nodes.
 * @param content Inline node array from a block.
 * @param lookup Case-insensitive context variable lookup.
 * @returns Normalized inline array and flag whether modifications occurred.
 */
const normalizeInlineContentArray = (
  content: EditorInlineContent[],
  lookup: ContextVariableLookup
): { normalized: EditorInlineContent[]; changed: boolean } => {
  let changed = false;
  const normalizedSegments: EditorInlineContent[] = [];

  content.forEach((segment) => {
    if (isStyledTextInlineContent(segment)) {
      const { segments, changed: segmentChanged } = convertTextNode(segment, lookup);

      if (segmentChanged) {
        normalizedSegments.push(...segments);
        changed = true;
      } else {
        normalizedSegments.push(segment);
      }

      return;
    }

    if (isContextTokenInlineContent(segment)) {
      const propsTokenValue = segment.props.token ?? '';
      const rawTokenProp =
        segment.props.rawToken || (/[{}]/.test(propsTokenValue) ? propsTokenValue : '');
      const sanitizedPropsToken = sanitizeContextToken(propsTokenValue);
      const sanitizedRawToken = sanitizeContextToken(rawTokenProp);
      const lookupKey = (sanitizedPropsToken || sanitizedRawToken).toLowerCase();
      const contextVariable = lookupKey ? lookup.get(lookupKey) : undefined;

      if (contextVariable) {
        normalizedSegments.push(createMentionNode(contextVariable));
        changed = true;
        return;
      }

      const isManualPlaceholder = /\{\{\s*\$/.test(rawTokenProp);
      const resolvedSanitizedToken = sanitizedPropsToken || sanitizedRawToken;

      const desiredRawToken =
        rawTokenProp ||
        (resolvedSanitizedToken
          ? isManualPlaceholder
            ? `{{$${resolvedSanitizedToken}}}`
            : `{{${resolvedSanitizedToken}}}`
          : '');

      const needsPropsUpdate =
        segment.props.token !== resolvedSanitizedToken ||
        segment.props.rawToken !== desiredRawToken;

      if (needsPropsUpdate) {
        normalizedSegments.push({
          ...segment,
          props: { ...segment.props, token: resolvedSanitizedToken, rawToken: desiredRawToken },
        });
        changed = true;
        return;
      }

      normalizedSegments.push(segment);
      return;
    }

    normalizedSegments.push(segment);
  });

  return changed
    ? { normalized: normalizedSegments, changed }
    : { normalized: content, changed: false };
};

/**
 * Walk through BlockNote blocks replacing inline tokens with mention nodes.
 * @param blocks Block tree originating from the editor.
 * @param lookup Case-insensitive context variable lookup.
 * @returns Updated block tree and change flag for the normalization pass.
 */
export const normalizeBlocksWithContextVariables = (
  blocks: EditorBlock[],
  lookup: ContextVariableLookup
): { normalized: EditorBlock[]; changed: boolean } => {
  let changed = false;

  const normalizedBlocks = blocks.map((block) => {
    const inlineContentResult = isInlineBlock(block)
      ? normalizeInlineContentArray(block.content, lookup)
      : undefined;

    const childrenResult =
      block.children.length > 0
        ? normalizeBlocksWithContextVariables(block.children, lookup)
        : undefined;

    const contentChanged = Boolean(inlineContentResult?.changed);
    const childrenChanged = Boolean(childrenResult?.changed);

    if (!contentChanged && !childrenChanged) {
      return block;
    }

    changed = true;

    if (contentChanged && isInlineBlock(block)) {
      return {
        ...block,
        content: inlineContentResult!.normalized,
        children: childrenResult?.normalized ?? block.children,
      } as EditorInlineBlock;
    }

    return { ...block, children: childrenResult?.normalized ?? block.children } as EditorBlock;
  });

  return { normalized: changed ? normalizedBlocks : blocks, changed };
};

/**
 * Normalize inline segments by converting `@tool(arg)` text into localTool nodes.
 */
const normalizeLocalToolInlineContentArray = (
  content: EditorInlineContent[]
): { normalized: EditorInlineContent[]; changed: boolean } => {
  let changed = false;
  const normalizedSegments: EditorInlineContent[] = [];

  content.forEach((segment) => {
    if (isStyledTextInlineContent(segment)) {
      const { segments, changed: segmentChanged } = convertLocalToolTextNode(segment);

      if (segmentChanged) {
        normalizedSegments.push(...segments);
        changed = true;
      } else {
        normalizedSegments.push(segment);
      }

      return;
    }

    normalizedSegments.push(segment);
  });

  return changed
    ? { normalized: normalizedSegments, changed }
    : { normalized: content, changed: false };
};

/**
 * Walk through BlockNote blocks converting `@tool_name(arg)` text into localTool inline nodes.
 * Runs as a second normalization pass after context variable normalization.
 */
export const normalizeBlocksWithLocalTools = (
  blocks: EditorBlock[]
): { normalized: EditorBlock[]; changed: boolean } => {
  let changed = false;

  const normalizedBlocks = blocks.map((block) => {
    const inlineContentResult = isInlineBlock(block)
      ? normalizeLocalToolInlineContentArray(block.content)
      : undefined;

    const childrenResult =
      block.children.length > 0 ? normalizeBlocksWithLocalTools(block.children) : undefined;

    const contentChanged = Boolean(inlineContentResult?.changed);
    const childrenChanged = Boolean(childrenResult?.changed);

    if (!contentChanged && !childrenChanged) {
      return block;
    }

    changed = true;

    if (contentChanged && isInlineBlock(block)) {
      return {
        ...block,
        content: inlineContentResult!.normalized,
        children: childrenResult?.normalized ?? block.children,
      } as EditorInlineBlock;
    }

    return { ...block, children: childrenResult?.normalized ?? block.children } as EditorBlock;
  });

  return { normalized: changed ? normalizedBlocks : blocks, changed };
};

/**
 * Resolve `trigger_workflow` argumentLabels from node_id → dialog name.
 * After normalization, `@trigger_workflow(node_id)` nodes have `argument = node_id`
 * and `argumentLabel = node_id`. This pass sets `argumentLabel` to the human-readable
 * dialog name so the badge displays the workflow name, not the UUID.
 */
export const resolveLocalToolLabels = (
  blocks: EditorBlock[],
  dialogs: Dialog[]
): { resolved: EditorBlock[]; changed: boolean } => {
  const nodeIdToName = new Map<string, string>();
  for (const dialog of dialogs) {
    const nodeId = dialog.nodes?.[0]?.node_id;
    if (nodeId) {
      nodeIdToName.set(nodeId, dialog.name);
    }
  }

  if (nodeIdToName.size === 0) {
    return { resolved: blocks, changed: false };
  }

  const resolveInlineContent = (
    content: EditorInlineContent[]
  ): { resolved: EditorInlineContent[]; changed: boolean } => {
    let changed = false;
    const result: EditorInlineContent[] = [];

    for (const segment of content) {
      if (
        segment.type === 'localTool' &&
        (segment as EditorLocalToolNode).props.toolName === 'trigger_workflow'
      ) {
        const props = (segment as EditorLocalToolNode).props;
        const dialogName = nodeIdToName.get(props.argument);
        if (dialogName && props.argumentLabel !== dialogName) {
          result.push({
            ...segment,
            props: { ...props, argumentLabel: dialogName },
          } as EditorInlineContent);
          changed = true;
          continue;
        }
      }
      result.push(segment);
    }

    return { resolved: result, changed };
  };

  let anyChanged = false;
  const resolvedBlocks = blocks.map((block) => {
    const inlineResult = isInlineBlock(block) ? resolveInlineContent(block.content) : undefined;
    const childResult =
      block.children.length > 0 ? resolveLocalToolLabels(block.children, dialogs) : undefined;

    const contentChanged = Boolean(inlineResult?.changed);
    const childrenChanged = Boolean(childResult?.changed);

    if (!contentChanged && !childrenChanged) {
      return block;
    }

    anyChanged = true;

    if (contentChanged && isInlineBlock(block)) {
      return {
        ...block,
        content: inlineResult!.resolved,
        children: childResult?.resolved ?? block.children,
      } as EditorInlineBlock;
    }

    return { ...block, children: childResult?.resolved ?? block.children } as EditorBlock;
  });

  return { resolved: anyChanged ? resolvedBlocks : blocks, changed: anyChanged };
};

// Whitespace before the first `<li>` makes the HTML parser drop the list's `start`.
const OL_LEADING_WHITESPACE_REGEX = /(<ol[^>]*>)\s+/g;

/**
 * Parse markdown into blocks, keeping the `start` number of ordered lists.
 * @param editor Editor instance whose schema the blocks are parsed against.
 * @param markdown Markdown to parse.
 */
export const parseMarkdownToBlocks = async <TBlock>(
  editor: { tryParseHTMLToBlocks: (html: string) => Promise<TBlock[]> },
  markdown: string
): Promise<TBlock[]> => {
  const html = await markdownToHTML(markdown || '');

  return editor.tryParseHTMLToBlocks(html.replace(OL_LEADING_WHITESPACE_REGEX, '$1'));
};

/**
 * Filter default slash menu items to the subset used by our editor.
 * Customizes the heading2 item to insert a heading with title text and an empty paragraph.
 * @param editor Active BlockNote editor instance.
 * @returns Slash menu suggestions limited to allowed aliases.
 */
export const getCustomSlashMenuItems = (
  editor: BlockNoteEditorInstance
): DefaultReactSuggestionItem[] => {
  const allowedAliases = new Set(['heading2', 'paragraph', 'numberedlist', 'bulletlist']);

  return getDefaultReactSlashMenuItems(editor)
    .filter((item) => item.aliases?.some((alias) => allowedAliases.has(alias)))
    .map((item) => {
      // Customize heading2 to match the behavior of the sticky navigation "+" button.
      // By default, BlockNote's heading2 creates an empty heading block. We override
      // this to create a heading with pre-filled "Guideline title" text followed by
      // an empty paragraph, providing a consistent guideline creation experience
      // regardless of whether the user clicks the navigation "+" or uses the slash menu.
      if (item.aliases?.includes('heading2')) {
        return {
          ...item,
          badge: undefined,
          onItemClick: async () => {
            const guidelineTitle = editor.dictionary.slash_menu.heading_2.title;
            // Create markdown with heading + empty paragraph to match sticky nav behavior
            const markdown = `## ${guidelineTitle}\n\n`;
            const blocksToInsert = await parseMarkdownToBlocks(editor, markdown);

            // Replace the current block (where "/" was typed) with the new guideline structure
            const currentBlock = editor.getTextCursorPosition().block;
            editor.insertBlocks(blocksToInsert, currentBlock, 'after');
            editor.removeBlocks([currentBlock]);

            // Position cursor at the empty paragraph for immediate content entry
            const insertedBlocks = editor.document;
            const headingIndex = insertedBlocks.findIndex((b) => b.id === blocksToInsert[0]?.id);
            if (headingIndex !== -1 && insertedBlocks[headingIndex + 1]) {
              editor.setTextCursorPosition(insertedBlocks[headingIndex + 1], 'end');
            }
          },
        };
      }
      return { ...item, badge: undefined };
    });
};

/**
 * Remove unsupported block types from the type selector dropdown.
 * @param editorDictionary Localized dictionary pulled from the editor instance.
 * @returns Filtered block type list excluding headings, quotes, and checklists.
 */
export function filterBlockTypeSelectItems(
  editorDictionary: BlockNoteEditorInstance['dictionary']
) {
  return [...blockTypeSelectItems(editorDictionary)].filter(
    (item) => !['Heading 1', 'Heading 2', 'Quote', 'Check List'].includes(item.name)
  );
}

/**
 * Normalize ordered lists in BlockNote blocks by removing custom start attributes.
 * @param blocks Array of BlockNote blocks to normalize.
 * @returns Normalized blocks alongside a flag indicating whether changes were applied.
 */
export function normalizeOrderedListsInBlocks<
  BSchema extends BlockSchema = BlockSchema,
  ISchema extends InlineContentSchema = InlineContentSchema,
  SSchema extends StyleSchema = StyleSchema,
>(
  blocks: Block<BSchema, ISchema, SSchema>[]
): { normalized: Block<BSchema, ISchema, SSchema>[]; changed: boolean } {
  let changed = false;

  const normalizedBlocks = blocks.map((block): Block<BSchema, ISchema, SSchema> => {
    const childrenResult =
      block.children.length > 0 ? normalizeOrderedListsInBlocks(block.children) : undefined;
    const childrenChanged = Boolean(childrenResult?.changed);
    const normalizedChildren = childrenResult?.normalized ?? block.children;

    if (block.type === 'numberedListItem') {
      const rawProps = block.props;
      let nextProps = rawProps;
      let propsChanged = false;

      if (rawProps) {
        const propsRecord = rawProps as Record<string, unknown>;
        const hasStartProp = 'start' in propsRecord;
        const hasDataStartProp = 'data-start' in propsRecord;

        if (hasStartProp || hasDataStartProp) {
          const clonedProps = { ...propsRecord };
          if (hasStartProp) {
            delete clonedProps.start;
          }
          if (hasDataStartProp) {
            delete clonedProps['data-start'];
          }
          nextProps = clonedProps as typeof rawProps;
          propsChanged = true;
        }
      }

      if (propsChanged || childrenChanged) {
        changed = true;

        const updatedBlock = {
          ...block,
          children: childrenChanged ? normalizedChildren : block.children,
        } as Block<BSchema, ISchema, SSchema>;

        if (propsChanged) {
          updatedBlock.props = nextProps;
        }

        return updatedBlock;
      }

      return block;
    }

    if (childrenChanged) {
      changed = true;

      return { ...block, children: normalizedChildren } as Block<BSchema, ISchema, SSchema>;
    }

    return block;
  });

  return changed
    ? { normalized: normalizedBlocks, changed: true }
    : { normalized: blocks, changed: false };
}

/**
 * Trim the trailing newline that blocksToMarkdownLossy appends to its output.
 * BlockNote's markdown serializer always ends with \n (a serialization artifact),
 * which would otherwise be counted as an extra character in length checks and counters.
 * @param markdown Raw markdown string from blocksToMarkdownLossy.
 * @returns Markdown string without trailing newline.
 */
export const trimMarkdown = (markdown: string): string => markdown.replace(/\n$/, '');

const UNSUPPORTED_PASTE_BLOCK_TYPES = new Set(['image', 'video', 'audio', 'file']);

/** `pasteMarkdown` is async, so clean up anyway once the paste has had this long to land. */
const PASTE_CLEANUP_TIMEOUT_MS = 2000;

/** Lifting children out of a stripped block can expose more, so strip repeatedly but not forever. */
const MAX_STRIP_PASSES = 10;

export function collectBlockIds<
  BSchema extends BlockSchema = BlockSchema,
  ISchema extends InlineContentSchema = InlineContentSchema,
  SSchema extends StyleSchema = StyleSchema,
>(blocks: Block<BSchema, ISchema, SSchema>[], ids = new Set<string>()): Set<string> {
  for (const block of blocks) {
    ids.add(block.id);
    collectBlockIds(block.children, ids);
  }

  return ids;
}

export function collectUnsupportedBlocks<
  BSchema extends BlockSchema = BlockSchema,
  ISchema extends InlineContentSchema = InlineContentSchema,
  SSchema extends StyleSchema = StyleSchema,
>(
  blocks: Block<BSchema, ISchema, SSchema>[],
  preexistingIds: Set<string>
): Block<BSchema, ISchema, SSchema>[] {
  const unsupported: Block<BSchema, ISchema, SSchema>[] = [];

  for (const block of blocks) {
    if (UNSUPPORTED_PASTE_BLOCK_TYPES.has(block.type) && !preexistingIds.has(block.id)) {
      unsupported.push(block);
    }

    if (block.children.length > 0) {
      unsupported.push(...collectUnsupportedBlocks(block.children, preexistingIds));
    }
  }

  return unsupported;
}

const stripUnsupportedBlocks = <
  BSchema extends BlockSchema,
  ISchema extends InlineContentSchema,
  SSchema extends StyleSchema,
>(
  editor: BlockNoteEditor<BSchema, ISchema, SSchema>,
  preexistingIds: Set<string>
) => {
  for (let pass = 0; pass < MAX_STRIP_PASSES; pass++) {
    const unsupported = collectUnsupportedBlocks(editor.document, preexistingIds);
    if (unsupported.length === 0) {
      return;
    }

    for (const block of unsupported) {
      // Removing an ancestor already took some of these, so a stale id is expected.
      try {
        editor.replaceBlocks([block.id], block.children);
      } catch {
        continue;
      }
    }
  }
};

export function createMediaStrippingPasteHandler<
  BSchema extends BlockSchema = BlockSchema,
  ISchema extends InlineContentSchema = InlineContentSchema,
  SSchema extends StyleSchema = StyleSchema,
>(): NonNullable<BlockNoteEditorOptions<BSchema, ISchema, SSchema>['pasteHandler']> {
  return ({ event, defaultPasteHandler, editor }) => {
    const plainText = event.clipboardData?.getData('text/plain') ?? '';
    if (plainText === '') {
      toast.error(t('rich_text_editor.errors.paste_failed'));
      return true;
    }

    const preexistingIds = collectBlockIds(editor.document);
    const pending: { unsubscribe?: () => void; timer?: ReturnType<typeof setTimeout> } = {};
    let hasCleanedUp = false;

    const cleanUp = () => {
      if (hasCleanedUp) {
        return;
      }
      hasCleanedUp = true;
      pending.unsubscribe?.();
      clearTimeout(pending.timer);

      // Deferred so we never dispatch inside the transaction that brought us here.
      queueMicrotask(() => {
        try {
          stripUnsupportedBlocks(editor, preexistingIds);

          const currentBlocks = editor.document;
          const { normalized, changed } = normalizeOrderedListsInBlocks(currentBlocks);
          if (changed) {
            editor.replaceBlocks(currentBlocks, normalized);
          }
        } catch (error) {
          console.error('Failed to clean up pasted blocks.', error);
        }
      });
    };

    // Subscribe before pasting: `pasteHTML` applies synchronously, `pasteMarkdown` does not.
    pending.unsubscribe = editor.onChange(cleanUp);
    pending.timer = setTimeout(cleanUp, PASTE_CLEANUP_TIMEOUT_MS);

    return defaultPasteHandler();
  };
}

/**
 * Convert localTool inline nodes back into `@tool(arg)` text nodes.
 * Must be called on blocks BEFORE passing them to `blocksToMarkdownLossy`
 * because the HTML→markdown pipeline strips custom HTML spans, losing tool data.
 */
const denormalizeLocalToolInlineContent = (
  content: EditorInlineContent[]
): { denormalized: EditorInlineContent[]; changed: boolean } => {
  let changed = false;
  const result: EditorInlineContent[] = [];

  for (const segment of content) {
    if (segment.type === 'localTool') {
      const { toolName, argument } = segment.props as { toolName: string; argument: string };
      const text = argument
        ? toolName === 'trigger_workflow'
          ? `@${toolName}(node_id="${argument}")`
          : `@${toolName}(${argument})`
        : `@${toolName}`;
      result.push({ type: 'text', text, styles: {} } as EditorInlineContent);
      changed = true;
    } else {
      result.push(segment);
    }
  }

  return { denormalized: result, changed };
};

/**
 * Walk block tree replacing localTool inline nodes with `@tool(arg)` text nodes.
 * Use this before `blocksToMarkdownLossy` so tool references survive the HTML→markdown conversion.
 */
export const denormalizeLocalToolNodes = (blocks: EditorBlock[]): EditorBlock[] => {
  let anyChanged = false;

  const result = blocks.map((block) => {
    const children =
      block.children.length > 0 ? denormalizeLocalToolNodes(block.children) : block.children;

    if (!isInlineBlock(block)) {
      if (children !== block.children) {
        anyChanged = true;
        return { ...block, children } as EditorBlock;
      }
      return block;
    }

    const { denormalized, changed } = denormalizeLocalToolInlineContent(block.content);

    if (!changed && children === block.children) {
      return block;
    }

    anyChanged = true;
    return {
      ...block,
      content: changed ? denormalized : block.content,
      children,
    } as EditorInlineBlock;
  });

  return anyChanged ? result : blocks;
};

/**
 * Replace mention/contextToken inline nodes with `{{$variable}}` text segments.
 * Without this, `blocksToMarkdownLossy` serializes the custom spans as empty bold
 * delimiters (`****`), losing the variable on save.
 */
const denormalizeContextVariableInlineContent = (
  content: EditorInlineContent[]
): { denormalized: EditorInlineContent[]; changed: boolean } => {
  let changed = false;
  const result: EditorInlineContent[] = [];

  for (const segment of content) {
    if (segment.type === 'mention') {
      const { contextVariable } = segment.props as { contextVariable: string };
      const text = formatContextVariableDisplay(contextVariable);
      result.push({ type: 'text', text, styles: {} } as EditorInlineContent);
      changed = true;
    } else if (segment.type === 'contextToken') {
      const { token, rawToken } = segment.props as { token: string; rawToken: string };
      const text = rawToken || formatContextVariableDisplay(token);
      result.push({ type: 'text', text, styles: {} } as EditorInlineContent);
      changed = true;
    } else {
      result.push(segment);
    }
  }

  return { denormalized: result, changed };
};

/**
 * Walk block tree replacing mention and contextToken inline nodes with `{{…}}` text nodes.
 * Use this before `blocksToMarkdownLossy` so variable references round-trip through the
 * HTML→markdown conversion instead of collapsing into empty bold markers.
 */
export const denormalizeContextVariableNodes = (
  blocks: EditorBlock[],
  depth = 0,
  maxDepth = 1000
): EditorBlock[] => {
  if (depth >= maxDepth) {
    return blocks;
  }

  let anyChanged = false;

  const result = blocks.map((block) => {
    const children =
      block.children.length > 0
        ? denormalizeContextVariableNodes(block.children, depth + 1, maxDepth)
        : block.children;

    if (!isInlineBlock(block)) {
      if (children !== block.children) {
        anyChanged = true;
        return { ...block, children } as EditorBlock;
      }
      return block;
    }

    const { denormalized, changed } = denormalizeContextVariableInlineContent(block.content);

    if (!changed && children === block.children) {
      return block;
    }

    anyChanged = true;
    return {
      ...block,
      content: changed ? denormalized : block.content,
      children,
    } as EditorInlineBlock;
  });

  return anyChanged ? result : blocks;
};
